/**
 * Moderator Privileges Editor Component
 * Alpine.js component for editing moderator role assignments
 */

import { editorBase } from '../alpine-editor-base.js';

const MODERATOR_ENDPOINT = '/v1/moderator';

/**
 * Moderator privileges editor component factory
 * @returns {Object} Alpine.js component
 */
export function moderatorPrivileges() {
    return {
        isOpen: false,
        userId: null,
        username: '',
        roles: {
            staff: false,
            admin: false,
            account: false,
            photo: false,
            post: false,
            dev: false
        },
        saving: false,
        error: null,

        open(userId, username, currentRoles) {
            this.userId = String(userId);
            this.username = username || 'Unknown';
            this.roles = {
                staff: currentRoles?.staff || false,
                admin: currentRoles?.admin || false,
                account: currentRoles?.account || false,
                photo: currentRoles?.photo || false,
                post: currentRoles?.post || false,
                dev: currentRoles?.dev || false
            };
            this.error = null;
            this.isOpen = true;
        },

        close() {
            this.isOpen = false;
            this.error = null;
        },

        async save() {
            if (this.saving || !this.userId) return;

            this.saving = true;
            this.error = null;

            try {
                const token = editorBase.getAuthToken();
                if (!token) {
                    throw new Error('Authentication required. Please refresh the page.');
                }

                // Build roles array from checked checkboxes
                const selectedRoles = [];
                if (this.roles.staff) selectedRoles.push('staff');
                if (this.roles.admin) selectedRoles.push('admin');
                if (this.roles.account) selectedRoles.push('account');
                if (this.roles.photo) selectedRoles.push('photo');
                if (this.roles.post) selectedRoles.push('post');
                if (this.roles.dev) selectedRoles.push('dev');

                const formData = new FormData();
                formData.append('roles', selectedRoles.join(','));

                const response = await fetch(`${MODERATOR_ENDPOINT}/${this.userId}`, {
                    method: 'PATCH',
                    headers: {
                        'X-AUTH-TOKEN': token
                    },
                    body: formData,
                    credentials: 'include'
                });

                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    throw new Error(data.message || 'Failed to update privileges');
                }

                this.close();
                window.location.reload();
            } catch (err) {
                this.error = err.message || 'An unexpected error occurred';
            } finally {
                this.saving = false;
            }
        }
    };
}
